import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import * as Actions from '../redux/articleActions';
import * as Selectors from '../redux/articleSelectors';
import * as CategorySelectors from '../../category/redux/categorySelectors';
import * as CommonSelectors from '../../../shared/redux/commonSelectors';
import { API_REQUEST_STATE } from '../../../core/enums/apiRequestState';
import { APP_CONFIG } from '../../../../config/appConfig';
import { objectHelper } from '../../../shared/utils/objectHelper';
import { CategoryGetResponse } from '../../category/model/categoryModel';
import { ArticleParams, ArticleValues } from '../model/articleModel';
import ArticleForm from '../form/ArticleForm';
import SnackbarSettings from '../../../components/Snackbar/SnackbarSettings';
import useMainProgressContext from '../../../components/progress/MainProgress/context/MainProgressContext';
import { ENTITY_ACTIONS } from '../../../components/form/base/EntityForm/enum/entityActions';

const getCategoryValue = (categoriesTree: CategoryGetResponse[], params: ArticleParams, index: number) => {
  const category = categoriesTree[index];

  if (category && category.id !== params.id!) {
    return {
      value: category.name,
      key: category.iri,
    };
  }

  return {
    value: '',
    key: '',
  };
};

const breadcrumbs = [
  {
    label: 'article.manage',
    to: null,
  },
];

let disableSuccessSnackbarAction = true;

const ArticleEdit: React.FC = () => {
  const params = useParams<ArticleParams>();
  const dispatch = useDispatch();
  const initialValues = useSelector(Selectors.initialValues);
  const categoriesTree = useSelector(CategorySelectors.categoriesTree);
  const rootCategory = useSelector(CommonSelectors.rootCategory);
  const apiRequestState = useSelector(Selectors.apiArticleRequestState);
  const apiRequestError = useSelector(Selectors.apiArticleRequestError);
  const { show, hide } = useMainProgressContext();
  const [articleAction, setArticleAction] = useState(ENTITY_ACTIONS.SAVE);

  useEffect(() => {
    dispatch(Actions.getArticle(params));
  }, [params]);

  useEffect(() => {
    if (initialValues.title.length && categoriesTree.length && apiRequestState !== API_REQUEST_STATE.LOADING) {
      breadcrumbs.push({
        label: categoriesTree[0].name,
        to: null,
      });

      hide();
    }
  }, [initialValues, categoriesTree]);

  useEffect(
    () => () => {
      dispatch(Actions.resetArticle());
    },
    [],
  );

  return (
    <>
      <SnackbarSettings
        successMessage={'article.snackbar.edit_success'}
        disableSuccessAction={disableSuccessSnackbarAction}
        apiRequestState={apiRequestState}
        apiRequestError={apiRequestError}
        setApiRequestState={Actions.setApiArticleRequestState}
        onSuccess={() => {
          if (articleAction === ENTITY_ACTIONS.PREVIEW) {
            window.open(APP_CONFIG.ARTICLE_PREVIEW_URL.replace(':id', params.id!), '_blank');
          }
        }}
      />
      <ArticleForm
        apiRequestState={apiRequestState}
        categoryId={rootCategory ? (rootCategory.value as unknown as number) : undefined}
        breadcrumbs={breadcrumbs}
        title="article.edit"
        initialValues={{
          ...initialValues,
          categoryOne: getCategoryValue(categoriesTree, params, 0),
          categoryTwo: getCategoryValue(categoriesTree, params, 1),
          categoryThree: getCategoryValue(categoriesTree, params, 2),
        }}
        onSubmit={(values: ArticleValues, _, type) => {
          show();
          setArticleAction(type!);
          disableSuccessSnackbarAction = false;

          const payload = objectHelper.removeByKeys<ArticleValues>(['categoryOne', 'categoryTwo', 'categoryThree'], {
            ...values,
            category: values.categoryThree.key
              ? values.categoryThree.key
              : values.categoryTwo.key
              ? values.categoryTwo.key
              : values.categoryOne.key,
          });
          dispatch(Actions.editArticle(params, payload));
        }}
      />
    </>
  );
};

export default ArticleEdit;
